import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getExamResult, getExam } from '../../services/exam';

const ExamResults = () => {
  const { resultId } = useParams();
  const [result, setResult] = useState(null);
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const resultData = await getExamResult(resultId);
        const examData = await getExam(resultData.examId);
        
        setResult(resultData);
        setExam(examData);
      } catch (error) {
        console.error('Error fetching result:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [resultId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getGradeColor = (percentage) => {
    if (percentage >= 80) return '#10b981'; // green
    if (percentage >= 60) return '#3b82f6'; // blue
    if (percentage >= 40) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  const getPerformanceLevel = (percentage) => {
    if (percentage >= 80) return 'Excellent';
    if (percentage >= 60) return 'Good';
    if (percentage >= 40) return 'Average';
    return 'Needs Improvement';
  };

  if (loading) {
    return (
      <div className="exam-results">
        <div className="loading-container">
          <div className="loading-spinner">Loading results...</div>
        </div>
      </div>
    );
  }

  if (!result || !exam) {
    return (
      <div className="exam-results">
        <div className="container">
          <div className="error-message">
            <h3>Result Not Found</h3>
            <p>The exam result you're looking for could not be found.</p>
            <Link to="/exams" className="btn btn-primary">
              Back to Exams
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const gradeColor = getGradeColor(result.percentage);

  return (
    <div className="exam-results">
      <div className="container">
        <div className="result-header">
          <h1>Exam Results</h1>
          <div className="exam-title">{exam.title}</div>
        </div>

        <div className="result-summary">
          <div className="score-display">
            <div className="score-circle" style={{ borderColor: gradeColor }}>
              <div className="score-percentage" style={{ color: gradeColor }}>
                {result.percentage}%
              </div>
              <div className="score-label">Score</div>
            </div>
            
            <div className="status-badge">
              {result.passed ? (
                <div className="passed">
                  <span className="status-icon">✅</span>
                  <span className="status-text">PASSED</span>
                </div>
              ) : (
                <div className="failed">
                  <span className="status-icon">❌</span>
                  <span className="status-text">FAILED</span>
                </div>
              )}
            </div>
          </div>

          <div className="performance-level">
            <h3>Performance Level</h3>
            <div className="level-text" style={{ color: gradeColor }}>
              {getPerformanceLevel(result.percentage)}
            </div>
          </div>
        </div>

        <div className="result-details">
          <div className="detail-grid">
            <div className="detail-card">
              <h4>Score Breakdown</h4>
              <div className="detail-item">
                <label>Your Score:</label>
                <span>{result.score} / {result.totalMarks}</span>
              </div>
              <div className="detail-item">
                <label>Percentage:</label>
                <span>{result.percentage}%</span>
              </div>
              <div className="detail-item">
                <label>Passing Marks:</label>
                <span>{exam.passingMarks}</span>
              </div>
              <div className="detail-item">
                <label>Correct Answers:</label>
                <span>
                  {(() => {
                    console.log('🔍 Debugging correct answers count:', {
                      resultAnswers: result.answers,
                      answersWithIsCorrect: result.answers.filter(a => a.isCorrect),
                      answersLength: result.answers.length,
                      correctCount: result.answers.filter(a => a.isCorrect).length
                    });
                    return `${result.answers.filter(a => a.isCorrect).length} / ${result.answers.length}`;
                  })()}
                </span>
              </div>
            </div>

            <div className="detail-card">
              <h4>Time Information</h4>
              <div className="detail-item">
                <label>Time Taken:</label>
                <span>{result.timeTaken} minutes</span>
              </div>
              <div className="detail-item">
                <label>Allowed Time:</label>
                <span>{exam.duration} minutes</span>
              </div>
              <div className="detail-item">
                <label>Started:</label>
                <span>{formatDate(result.startedAt)}</span>
              </div>
              <div className="detail-item">
                <label>Completed:</label>
                <span>{formatDate(result.completedAt)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="answer-review">
          <h3>Answer Review</h3>
          <div className="questions-review">
            {exam.questions.map((question, index) => {
              const userAnswer = result.answers[index];
              const isCorrect = userAnswer?.isCorrect || false;
              
              return (
                <div key={index} className={`question-review ${isCorrect ? 'correct' : 'incorrect'}`}>
                  <div className="question-header">
                    <div className="question-number">Q{index + 1}</div>
                    <div className="question-status">
                      {isCorrect ? '✅ Correct' : '❌ Incorrect'}
                    </div>
                    <div className="question-marks">{userAnswer?.marks || 0}/{question.marks}</div>
                  </div>
                  
                  <div className="question-content">
                    <h4>{question.question}</h4>
                    
                    <div className="answer-comparison">
                      <div className="your-answer">
                        <label>Your Answer:</label>
                        <div className="answer-text">
                          {question.type === 'multiple_choice' && (
                            <span>{question.options[userAnswer?.answer]}</span>
                          )}
                          {question.type === 'true_false' && (
                            <span>{userAnswer?.answer ? 'True' : 'False'}</span>
                          )}
                          {question.type === 'short_answer' && (
                            <span>{userAnswer?.answer || 'Not answered'}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="correct-answer">
                        <label>Correct Answer:</label>
                        <div className="answer-text">
                          {question.type === 'multiple_choice' && (
                            <span>{question.options[question.correctAnswer]}</span>
                          )}
                          {question.type === 'true_false' && (
                            <span>{question.correctAnswer ? 'True' : 'False'}</span>
                          )}
                          {question.type === 'short_answer' && (
                            <span>{question.correctAnswer}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="result-actions">
          <div className="action-buttons">
            <Link to="/exams" className="btn btn-secondary">
              Back to Exams
            </Link>
            <Link to="/dashboard" className="btn btn-primary">
              Go to Dashboard
            </Link>
          </div>
          
          <div className="recommendations">
            <h3>Recommendations</h3>
            {result.passed ? (
              <div className="recommendation success">
                <p>🎉 Great job! You passed the exam. Keep up the good work and continue practicing to maintain your performance.</p>
              </div>
            ) : (
              <div className="recommendation improvement">
                <p>💪 Don't give up! Review the questions you got wrong and practice more. Consider watching video lessons on topics you struggled with.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamResults;
